import { inject, Injectable } from '@angular/core';
import { clamp, clone, isEqual, maxBy } from 'lodash';
import { ViewBox } from '../../../team-apis/content/view-box';
import { AbstractSensorContentStore2D } from '../state/abstract-sensor-content-store-2d';
import { ShapeBoundingPointsService } from './shape-bounding-points.service';
import { ViewBoxBuilderService } from './view-box-builder.service';
import { ViewBoxPaddingService } from './view-box-padding.service';
import {
  MAX_ZOOM_SIZE_MULTIPLIER,
  MIN_ZOOM_SIZE_MULTIPLIER,
} from './view-box.service';

@Injectable()
export class ViewBoxClampingService {
  private store = inject(AbstractSensorContentStore2D);
  private shapeBoundingPointsService = inject(ShapeBoundingPointsService);
  private viewBoxBuilderService = inject(ViewBoxBuilderService);
  private viewBoxPaddingService = inject(ViewBoxPaddingService);

  async clampViewBoxWithPadding(viewBoxToClamp: ViewBox) {
    const clampedViewBox = this.clampViewBox(viewBoxToClamp);

    const focusedShapeStore = this.store.getFocusedShapeStore();
    const focusedShapeBoundingPoints =
      focusedShapeStore &&
      this.shapeBoundingPointsService.getBoundingPoints(focusedShapeStore);

    // bounding points are equal for the point shape, no padding for those
    if (
      !focusedShapeBoundingPoints ||
      isEqual(focusedShapeBoundingPoints[0], focusedShapeBoundingPoints[1])
    ) {
      return clampedViewBox;
    }

    const paddedBoundingPoints =
      this.viewBoxPaddingService.addPaddingToBoundingPoints(
        focusedShapeBoundingPoints,
        1.2,
      );
    const paddedViewBox =
      await this.viewBoxBuilderService.buildFromBoundingPoints(
        paddedBoundingPoints,
      );
    const clampedPaddedViewBox = this.clampViewBox(paddedViewBox);

    return maxBy(
      [clampedViewBox, clampedPaddedViewBox],
      (viewBox) => viewBox[2] * viewBox[3],
    )!;
  }

  /**
   * Adjusts the viewBox dimensions to fit within zoom limits while maintaining the original aspect ratio.
   *
   * The function calculates min/max zoom sizes, adjusts min/max dimensions based on the viewBox's aspect ratio,
   * clamps the viewBox dimensions within these limits, and repositions the viewBox if its size was adjusted.
   */
  clampViewBox(viewBox: ViewBox) {
    const { minZoomSize, maxZoomSize } = this.getZoomLimits();
    const aspectRatio = viewBox[2] / viewBox[3];
    const minDimension = [minZoomSize, minZoomSize];
    const maxDimension = [maxZoomSize, maxZoomSize];
    if (aspectRatio < 1) {
      minDimension[1] = minDimension[0] / aspectRatio;
      maxDimension[1] = maxDimension[0] / aspectRatio;
    } else {
      minDimension[0] = minDimension[1] * aspectRatio;
      maxDimension[0] = maxDimension[1] * aspectRatio;
    }

    const clampedSize = [
      clamp(viewBox[2], minDimension[0], maxDimension[0]),
      clamp(viewBox[3], minDimension[1], maxDimension[1]),
    ];
    const clampedViewBox = clone(viewBox);
    if (clampedSize[0] !== viewBox[2] || clampedSize[1] !== viewBox[3]) {
      const centerPosition = [
        viewBox[0] + viewBox[2] / 2,
        viewBox[1] + viewBox[3] / 2,
      ];
      clampedViewBox[0] = centerPosition[0] - clampedSize[0] / 2;
      clampedViewBox[1] = centerPosition[1] - clampedSize[1] / 2;
      clampedViewBox[2] = clampedSize[0];
      clampedViewBox[3] = clampedSize[1];
    }

    return clampedViewBox;
  }

  private getZoomLimits() {
    const minZoomSize = MIN_ZOOM_SIZE_MULTIPLIER * this.store.state().pixelSize;
    const maxZoomSize =
      Math.max(
        this.store.state().sensorRendererState.height,
        this.store.state().sensorRendererState.width,
      ) * MAX_ZOOM_SIZE_MULTIPLIER;

    return { minZoomSize, maxZoomSize };
  }
}
